import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft, CalendarDays, MapPin, Check, X, Loader2, Plane, ShieldCheck,
  FileText, Clock, ArrowRight, BedDouble,
} from "lucide-react";
import { LeadCapture } from "@/components/site/LeadCapture";
import { Footer } from "@/components/site/Footer";
import { trackEvent, scrollToId } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const fmtDates = (o) => {
  if (!o?.start_date && !o?.end_date) return null;
  const f = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";
  return [f(o.start_date), f(o.end_date)].filter(Boolean).join(" – ");
};

const Section = ({ id, eyebrow, title, children, className = "" }) => (
  <section id={id} className={`py-14 md:py-20 ${className}`}>
    <div className="max-w-5xl mx-auto px-6 md:px-12">
      {eyebrow && <p className="uppercase text-sm tracking-[0.2em] font-semibold text-terracotta mb-3">{eyebrow}</p>}
      {title && <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-ink mb-8">{title}</h2>}
      {children}
    </div>
  </section>
);

export default function Offer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(undefined); // undefined=loading, null=not found
  const [content, setContent] = useState({});
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/offers/${slug}`);
        if (!alive) return;
        setOffer(data);
        trackEvent("offer_view", { location: "offer_page", offer_id: data.id, offer_title: data.title });
        const c = await axios.get(`${API}/site-content`).catch(() => ({ data: {} }));
        if (alive) setContent(c.data || {});
        if (data.show_related) {
          const all = await axios.get(`${API}/offers`, { params: { limit: 6 } }).catch(() => ({ data: [] }));
          if (alive) setRelated((all.data || []).filter((o) => o.id !== data.id).slice(0, 3));
        }
      } catch {
        if (alive) setOffer(null);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (offer) {
      document.title = `${offer.title} | KIS Living Vacations`;
      const desc = offer.short_description || offer.full_description || "Curated travel by KIS Living Vacations.";
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.setAttribute("content", desc.slice(0, 160));
    }
    return () => { document.title = "KIS Living Vacations | Beautiful Travel Made Simple"; };
  }, [offer]);

  if (offer === undefined) {
    return <div className="min-h-screen bg-sand flex items-center justify-center"><Loader2 className="animate-spin text-terracotta" size={32} /></div>;
  }

  if (offer === null) {
    return (
      <div data-testid="offer-not-found" className="min-h-screen bg-sand flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-serif text-4xl font-semibold text-ink mb-3">Trip not found</h1>
        <p className="text-ink-soft mb-6">This experience may no longer be available.</p>
        <Button onClick={() => navigate("/")} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">Back to homepage</Button>
      </div>
    );
  }

  const dates = fmtDates(offer);
  const handleCta = () => {
    trackEvent("cta_click", { location: "offer_page", cta: offer.cta_label || "Request Information", offer_id: offer.id });
    if (offer.cta_action === "external_link" && offer.cta_url) {
      window.open(offer.cta_url, "_blank", "noopener");
      return;
    }
    scrollToId("lead-capture");
  };

  const included = (offer.whats_included || []).filter(Boolean);
  const notIncluded = (offer.whats_not_included || []).filter(Boolean);
  const itinerary = (offer.itinerary || []).filter((s) => s.title || s.detail);
  const gallery = (offer.gallery || []).filter(Boolean);
  const faqs = (offer.offer_faqs || []).filter((f) => f.question);

  const goodToKnow = [
    { icon: Plane, label: "Flights", text: content.flights_disclaimer },
    { icon: ShieldCheck, label: "Travel insurance", text: content.insurance_note },
    { icon: FileText, label: "Booking terms", text: content.booking_terms },
    { icon: Clock, label: "Cancellations", text: offer.cancellation_notes || content.cancellation_policy },
  ].filter((x) => x.text);

  return (
    <div data-testid="offer-page" className="bg-sand min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[72vh] flex items-end">
        {offer.image_url
          ? <img src={resolveImg(offer.image_url)} alt={offer.title} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 bg-sage" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-ink/30" />
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-12 pb-14 pt-28">
          <Link to="/" data-testid="offer-back-link" className="inline-flex items-center gap-2 text-white/85 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> KIS Living
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="bg-terracotta text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">{offer.travel_type}</span>
            <span className="bg-white/90 text-ink text-xs font-semibold px-3 py-1 rounded-full">{offer.status}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="offer-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white max-w-3xl"
          >
            {offer.title}
          </motion.h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-white/90">
            {dates && <span className="flex items-center gap-2"><CalendarDays size={18} /> {dates}</span>}
            {offer.destinations?.length > 0 && <span className="flex items-center gap-2"><MapPin size={18} /> {offer.destinations.join(" • ")}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Button data-testid="offer-hero-cta" onClick={handleCta} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-8 py-6 h-auto text-base">
              {offer.cta_label || "Request Information"}
            </Button>
            {offer.starting_price && (
              <span className="text-white text-lg">
                From <span className="font-semibold">{offer.starting_price}</span>
                {offer.pricing_note && <span className="text-white/70 text-sm"> · {offer.pricing_note}</span>}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Overview */}
      {offer.full_description && (
        <Section eyebrow="The experience" title="About this trip" className="bg-sand">
          <p data-testid="offer-overview" className="text-lg leading-relaxed text-ink-soft whitespace-pre-line max-w-3xl">{offer.full_description}</p>
        </Section>
      )}

      {/* Included / Not included */}
      {(included.length > 0 || notIncluded.length > 0) && (
        <Section eyebrow="Details" title="What's included" className="bg-[#EAE4DA]/40">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {included.length > 0 && (
              <div data-testid="offer-included" className="bg-white rounded-2xl border border-bone p-7">
                <h3 className="font-serif text-xl font-semibold text-ink mb-4">Included</h3>
                <ul className="space-y-3">
                  {included.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-ink-soft">
                      <Check size={18} className="text-sage mt-0.5 shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {notIncluded.length > 0 && (
              <div data-testid="offer-not-included" className="bg-white rounded-2xl border border-bone p-7">
                <h3 className="font-serif text-xl font-semibold text-ink mb-4">Not included</h3>
                <ul className="space-y-3">
                  {notIncluded.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-ink-soft">
                      <X size={18} className="text-terracotta mt-0.5 shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Accommodation */}
      {offer.show_accommodation && offer.accommodation && (
        <Section eyebrow="Where you'll stay" title="Accommodation" className="bg-sand">
          <div data-testid="offer-accommodation" className="flex items-start gap-4 max-w-3xl">
            <BedDouble size={26} className="text-terracotta shrink-0 mt-1" />
            <p className="text-lg leading-relaxed text-ink-soft whitespace-pre-line">{offer.accommodation}</p>
          </div>
        </Section>
      )}

      {/* Itinerary */}
      {offer.show_itinerary && itinerary.length > 0 && (
        <Section eyebrow="Day by day" title="Itinerary" className="bg-[#EAE4DA]/40">
          <div data-testid="offer-itinerary" className="space-y-4 max-w-3xl">
            {itinerary.map((s, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-terracotta text-white text-sm font-semibold shrink-0">{i + 1}</span>
                  {i < itinerary.length - 1 && <span className="w-px flex-1 bg-bone mt-1" />}
                </div>
                <div className="pb-6">
                  {s.title && <h4 className="font-serif text-xl font-medium text-ink">{s.title}</h4>}
                  {s.detail && <p className="text-ink-soft mt-1 leading-relaxed whitespace-pre-line">{s.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Gallery */}
      {offer.show_gallery && gallery.length > 0 && (
        <Section eyebrow="A closer look" title="Gallery" className="bg-sand">
          <div data-testid="offer-gallery" className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((g, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl overflow-hidden bg-bone">
                <img src={resolveImg(g)} alt={`${offer.title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Good to know (shared write-once content) */}
      {goodToKnow.length > 0 && (
        <Section eyebrow="Before you book" title="Good to know" className="bg-sage">
          <div data-testid="offer-good-to-know" className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {goodToKnow.map((g, i) => {
              const Icon = g.icon;
              return (
                <div key={i} className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 text-white mb-2">
                    <Icon size={20} /> <span className="font-semibold">{g.label}</span>
                  </div>
                  <p className="text-white/85 leading-relaxed">{g.text}</p>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* FAQs */}
      {offer.show_faqs && faqs.length > 0 && (
        <Section eyebrow="Questions" title="Frequently asked" className="bg-sand">
          <Accordion type="single" collapsible data-testid="offer-faqs" className="max-w-3xl">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-bone">
                <AccordionTrigger className="text-left font-serif text-xl font-medium text-ink hover:no-underline py-5">{f.question}</AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-ink-soft pb-5 whitespace-pre-line">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      )}

      {/* Meet Juliet (shared write-once content) */}
      {offer.show_meet_juliet && content.founder_note && (
        <Section className="bg-[#EAE4DA]/40">
          <div data-testid="offer-meet-juliet" className="flex flex-col md:flex-row items-center gap-10 max-w-4xl">
            {content.founder_image && (
              <div className="w-40 h-52 rounded-2xl overflow-hidden bg-bone shrink-0 shadow-md">
                <img src={resolveImg(content.founder_image)} alt={content.founder_name} className="w-full h-full object-cover" />
              </div>
            )}
            <div>
              <p className="uppercase text-sm tracking-[0.2em] font-semibold text-terracotta mb-3">{content.founder_heading || "A Note From Juliet"}</p>
              <p className="text-lg leading-relaxed text-ink-soft whitespace-pre-line">{content.founder_note}</p>
              <p className="mt-4 font-serif text-xl text-ink">{content.founder_name} <span className="text-ink-soft text-base">— {content.founder_title}</span></p>
            </div>
          </div>
        </Section>
      )}

      {/* Inquiry form */}
      <LeadCapture presetInterest={offer.travel_type} presetOfferId={offer.id} presetOfferTitle={offer.title} />

      {/* Related trips */}
      {offer.show_related && related.length > 0 && (
        <Section eyebrow="Keep exploring" title="More experiences" className="bg-sand">
          <div data-testid="offer-related" className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((o) => (
              <Link key={o.id} to={`/offers/${o.slug || o.id}`} data-testid={`related-offer-${o.id}`}
                className="group overflow-hidden rounded-3xl bg-white border border-bone shadow-sm hover:shadow-xl transition-shadow duration-500 flex flex-col">
                <div className="overflow-hidden">
                  {o.image_url
                    ? <img src={resolveImg(o.image_url)} alt={o.title} loading="lazy" className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-700" />
                    : <div className="w-full h-48 bg-bone" />}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-xs text-terracotta font-semibold uppercase">{o.travel_type}</p>
                  <h3 className="font-serif text-xl font-semibold text-ink mt-1">{o.title}</h3>
                  {o.short_description && <p className="text-sm text-ink-soft mt-2 flex-1 line-clamp-2">{o.short_description}</p>}
                  <span className="mt-4 inline-flex items-center gap-1 text-terracotta font-semibold text-sm">
                    View trip <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <Footer />
    </div>
  );
}
